<template>
  <f7-list form strong-ios dividers-ios outline-ios @submit.prevent="goToSummary">
    <f7-list-input
      v-model:value="selectedDate"
      error-message="Toto pole je povinné"
      :error-message-force="validation.datePicker"
      type="datepicker"
      label="Dátum parkovania"
      placeholder="Vyberte dátum"
      readonly
      required
      :calendar-params="calendarParams"
      @calendar:change="onDayClick"
    />
    <f7-list-input
      :value="formattedTime"
      input-id="timePicker"
      error-message="Toto pole je povinné"
      :error-message-force="validation.timePicker"
      type="text"
      label="Čas parkovania"
      :placeholder="isFetchingSlots ? 'Načítavam dostupné časy...' : 'Vyberte časový slot'"
      readonly
      required
      :disabled="isFetchingSlots || !availableTimeSlots.values.length"
      :info="
        !availableTimeSlots.values.length ? 'Čas bude možné vybrať až po zvolení dátumu' : undefined
      "
      @click="
        selectedTime =
          selectedTime || availableTimeSlots.values[timePicker.cols[0]?.activeIndex || 0]
      "
      @change="onTimeChange"
    />
    <f7-block>
      <f7-button large round tonal color="secondary" type="submit">Pokračovať na sumár</f7-button>
    </f7-block>
  </f7-list>
</template>

<script setup lang="ts">
import { f7, useStore } from 'framework7-vue'
import { DateTime } from 'luxon'
import { computed, inject, onMounted, reactive, ref, watch } from 'vue'

import { refreshBanStatus } from '@/assets/composables/ban'
import { getAvailableSlots } from '@/assets/composables/endpoints'
import { ToReadableTime } from '@/assets/composables/mappers'
import type { AvailableSlots, PartOfDay } from '@/assets/composables/types'
import { notify } from '@/assets/composables/utils'

const {
  currentDate,
  selectedDate,
  selectedReservationDateIso,
  selectedTime,
  formattedTime,
  validation,
  validateForm,
} = inject<any>('formData')

let timePicker: any

const emit = defineEmits(['continue'])
const props = defineProps({
  removeInnerData: {
    type: Boolean,
    default: false,
  },
})

const isFetchingSlots = ref(false)
const defaultSettings = useStore('defaultSettings')
const banInfo = useStore('banInfo')

const toPickerBoundary = (date: DateTime, endOfDay = false): Date => {
  const boundary = endOfDay ? date.endOf('day') : date.startOf('day')
  return boundary.setZone('system', { keepLocalTime: true }).toJSDate()
}

const calendarParams = computed(() => {
  const today = currentDate.value.startOf('day')
  const maxAllowedResDateDays = Number(defaultSettings.value.maxAllowedResDate || 0)
  const maxDate = today.plus({ days: maxAllowedResDateDays })

  // The calendar is not restricted by the ban — banned days stay pickable and the
  // user gets an explanatory notice on selection (see onDayClick). Restricting the
  // range here would collapse the whole calendar when the ban outlasts maxDate.
  return {
    locale: 'sk-SK',
    dateFormat: { weekday: 'long', month: 'long', day: '2-digit', year: 'numeric' },
    minDate: toPickerBoundary(today),
    maxDate: toPickerBoundary(maxDate, true),
    yearSelector: false,
    monthPicker: false,
    closeOnSelect: true,
  }
})

// isDateWithinBan compares plain calendar dates (UTC) so it matches the backend's
// USER_BANNED rule exactly: a date on or before the ban end is blocked.
const isDateWithinBan = (dateIso: string): boolean => {
  const bannedUntil = banInfo.value?.bannedUntil
  if (!bannedUntil?.isValid) return false
  const banEndDate = bannedUntil.toUTC().toISODate()
  const selectedDay = DateTime.fromISO(dateIso).toISODate()
  return !!banEndDate && !!selectedDay && selectedDay <= banEndDate
}

const availableTimeSlots = reactive({
  textAlign: 'center',
  rotateEffect: true,
  values: [] as PartOfDay[],
  displayValues: [] as string[],
})

onMounted(() => {
  timePicker = f7.picker.create({
    inputEl: '#timePicker',
    cols: [availableTimeSlots],
    renderToolbar: () => {
      return `
        <div class="toolbar">
          <div class="toolbar-inner">
            <div class="left"></div>
            <div class="right">
              <a href="#" class="link sheet-close popover-close">Vybrať</a>
            </div>
          </div>
        </div>
      `
    },
  })
})

const resetComponentBasedTimeData = () => {
  availableTimeSlots.values = []
  availableTimeSlots.displayValues = []
}

const isDateAvailable = async (dateIso: string): Promise<AvailableSlots | null> => {
  const slots: AvailableSlots = await getAvailableSlots(dateIso)

  if (!slots.isAnyAvailable) {
    selectedDate.value = []
    notify(
      `Pre zvolený dátum <b>${DateTime.fromISO(dateIso, { zone: 'Europe/Bratislava' }).toFormat('dd. MM (cccc)')}</b> už nie sú dostupné žiadne časové sloty. Vyberte si prosím iný dátum.`,
    )
    return null
  }

  return slots
}

const onTimeChange = (e: Event) => {
  selectedTime.value = (e.target as HTMLInputElement).value as PartOfDay
}

const onDayClick = async (calendar: Date[]) => {
  if (calendar.length === 0) return
  if (!selectedReservationDateIso.value) return
  resetComponentBasedTimeData()

  // Re-validate the ban fresh on every date pick, then block dates inside the
  // ban window with an explanatory notice (same style as the "no slots" notice).
  await refreshBanStatus()
  if (isDateWithinBan(selectedReservationDateIso.value)) {
    const until = banInfo.value.bannedUntil.toUTC().toLocaleString(DateTime.DATE_FULL)
    selectedDate.value = []
    resetComponentBasedTimeData()
    notify(
      `Tento deň si nemôžete zarezervovať. Za nevrátenie parkovacej karty máte zákaz rezervovania do <b>${until}</b>. Vyberte si prosím dátum po tomto dátume.`,
    )
    return
  }

  try {
    isFetchingSlots.value = true
    const slots = await isDateAvailable(selectedReservationDateIso.value)
    if (slots) {
      availableTimeSlots.values = slots.values
      availableTimeSlots.displayValues = slots.displayValues
    }
    if (!availableTimeSlots.values.includes(selectedTime.value)) {
      selectedTime.value = null
    }
  } catch (error) {
    selectedDate.value = []
    notify('Nepodarilo sa zistiť dostupnosť dátumu. Skúste to prosím neskôr.')
  } finally {
    isFetchingSlots.value = false
  }
}

const goToSummary = () => {
  if (validateForm()) {
    emit('continue')
  }
}

watch(selectedTime, (newValue: PartOfDay) => {
  formattedTime.value = ToReadableTime(newValue, false)
})

watch(
  () => props.removeInnerData,
  (newValue: boolean) => {
    if (newValue) {
      resetComponentBasedTimeData()
    }
  },
)
</script>
